/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @typescript-eslint/naming-convention */
import { gql } from 'graphql-request';
import uniqBy from 'lodash/uniqBy';
import { useRouter } from 'next/router';
import { useInfiniteQuery, useQuery } from 'react-query';
import React from 'react';
import UserItem from '~/components/UserItem';
import { useDataContext } from '~/contexts/Data';
import { getItemsFromQuery, getNextPageParam } from '~/hooks/useSearchObjkts';
import FetchMoreButton from './FetchMoreButton';
import { User } from '~/types';
import Button from './Button';
import StatusText from './StatusText';
import { useTransformUser } from '~/graphql';
import { useSetFilterQueryParams } from '~/hooks/useFilter';
import LoadIcon from './LoadIcon';
import useGraphqlClient from '~/hooks/useGraphqlClient';

const userFragment = `
  name
  address
  description
  metadata
  metadata_file
`;

const UserSearchQuery = gql`
  query UserSearchQuery(
    $where: holder_bool_exp,
    $limit: Int,
    $offset: Int,
    $order_by: [holder_order_by!],
  ) {
    holder(
      offset: $offset,
      where: $where,
      limit: $limit,
      order_by: $order_by
    ) {
      ${userFragment}
    }
  }
`;

const useArtistSearch = (q: string, showBannedWallets: boolean) => {
  const { blockedWallets } = useDataContext();
  const gqlClient = useGraphqlClient();
  const query = useInfiniteQuery(
    ['users', q, showBannedWallets, ...blockedWallets],
    async ({ pageParam: offset }) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { holder = [] } = await gqlClient(
        UserSearchQuery,
        {
          offset,
          limit: 20,
          order_by: {
            tokens_aggregate: {
              count: 'desc',
            },
          },
          where: {
            ...(q ? {
              _or: [
                {
                  name: {
                    _ilike: `${encodeURIComponent(q)}`,
                  },
                },
                {
                  name: {
                    _ilike: `%${encodeURIComponent(q)}%`,
                  },
                },
              ],
            } : {}),
            ...(showBannedWallets ? {} : {
              address: {
                _nin: blockedWallets,
              },
            }),
          },
        },
      );
      return uniqBy([...holder], 'address');
    },
    {
      getNextPageParam,
    },
  );
  const transformUser = useTransformUser();
  const artists = getItemsFromQuery<User>(query).map(transformUser);
  return { ...query, artists };
};

const UserSearchCountQuery = gql`
  query UserSearchQuery(
    $where: holder_bool_exp,
  ) {
    holder_aggregate(
      where: $where,
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const useArtistSearchCount = (q: string, showBannedWallets: boolean) => {
  const { blockedWallets } = useDataContext();
  const gqlClient = useGraphqlClient();
  const query = useQuery(
    ['users', 'count', q, showBannedWallets, ...blockedWallets],
    async () => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { holder_aggregate } = await gqlClient(
        UserSearchCountQuery,
        {
          where: {
            ...(q ? {
              name: {
                _ilike: `%${encodeURIComponent(q)}%`,
              },
            } : {}),
            ...(showBannedWallets ? {} : {
              address: {
                _nin: blockedWallets,
              },
            }),
          },
        },
      );
      return holder_aggregate?.aggregate?.count;
    },
    {
      enabled: !!q,
    },
  );
  return {
    ...query,
    count: query.data,
    countLoading: query.status === 'loading',
  };
};

const SearchArtists = () => {
  const router = useRouter();
  const { q, showBanned } = router.query as { q: string; showBanned: string; };
  const {
    status,
    artists = [],
    fetchNextPage,
    isFetchingNextPage,
  } = useArtistSearch(q, !!showBanned);
  const { count } = useArtistSearchCount(q, !!showBanned);
  const canFetchMore = artists.length > 0 && artists.length < count;
  const setFilterQueryParams = useSetFilterQueryParams();
  const handleChangeShowBanned = () => {
    setFilterQueryParams({ showBanned: showBanned ? null : true });
  };
  return (
    <div style={ { marginTop: 15 } }>
      {
        status === 'loading'
          ? <LoadIcon $animating />
          : status === 'error'
            ? <StatusText>Something went wrong</StatusText>
            : artists?.length === 0 ? <StatusText>Nothing found</StatusText> : (
              <>
                <div
                  onClick={ handleChangeShowBanned }
                  style={
                    {
                      marginTop: 15,
                      marginBottom: 10,
                    }
                  }
                >
                  <Button $primary={ !!showBanned } onClick={ handleChangeShowBanned }>
                    Show banned artists
                  </Button>
                </div>
                {
                  uniqBy(artists, (u) => u.address).map((user) => (
                    <UserItem key={ `user.item.${user.address}` } { ...user } />
                  ))
                }
                {
                  canFetchMore ? (
                    <FetchMoreButton { ...{ fetchNextPage, isFetchingNextPage } } />
                  ) : null
                }
              </>
            )
      }
    </div>
  );
};

export default SearchArtists;
